import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { verifyCode, getReceiptPaymentFormParams, checkPaidReceipts } from "../actions/other/receiptsActions";
import { AppDispatch, RootState } from "../store";
import Loader from "../components/loaders/Loader";
import Swal from "sweetalert2";
import AppNavbar from "../components/menus/AppNavbar";

interface Props {
    code: string
    onExpired: () => void
}

export default function ReceiptList({ code, onExpired }: Props) {
    const { t } = useTranslation()
    const dispatch: AppDispatch = useDispatch()
    const formRef = useRef<HTMLFormElement>(null)

    const { receipts, loading, resultreceipt, formParams, paidReceiptIds } = useSelector((state: RootState) => state.receipts)

    const [redsysParams, setRedsysParams] = useState<any>(null)
    const [payingReceiptId, setPayingReceiptId] = useState<string | null>(null)

    // Verificación del código inicial
    useEffect(() => {
        dispatch(verifyCode(code) as any)
    }, [code, dispatch])

    // Comprobamos si hay recibos pagados
    useEffect(() => {
        if (receipts.length > 0) {
            dispatch(checkPaidReceipts(receipts.map((r: any) => String(r.id))) as any)
        }
    }, [receipts])

    // Si el código ha expirado
    useEffect(() => {
        if (resultreceipt === 'error') {
            onExpired()
        }
    }, [resultreceipt])

    // Cuando llegan los parámetros de Redsys
    useEffect(() => {
        if (formParams) {
            if (formParams?.params) {
                setRedsysParams(formParams.params)
                dispatch({ type: 'RECEIPT_GET_PAYMENT_FORM_RESET' })
            } else {
                setPayingReceiptId(null)
                Swal.fire({
                    showConfirmButton: true,
                    icon: 'error',
                    text: t('receiptTranslate.' + formParams?.message)
                })
            }
        }
    }, [formParams, dispatch, t])

    // Auto-submit cuando los parámetros están listos
    useEffect(() => {
        if (redsysParams?.Ds_MerchantParameters) {
            formRef.current?.submit()
        }
    }, [redsysParams])

    const handlePay = (receiptId: string, importeTotal: number) => {
        setPayingReceiptId(receiptId)
        dispatch(getReceiptPaymentFormParams(receiptId, importeTotal) as any)
    }

    const formatDate = (date: string) => {
        return new Intl.DateTimeFormat('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(date))
    }

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
        }).format(amount)
    }

    return (
        <>
            <AppNavbar />
            {/* Formulario oculto de Redsys */}
            {redsysParams && (
                <form
                    autoComplete='off'
                    name="form"
                    action="https://sis-t.redsys.es:25443/sis/realizarPago" /* cambiar a producción cuando toque */
                    method="POST"
                    ref={formRef}
                    style={{ display: 'none' }}
                >
                    <input type="hidden" name="Ds_SignatureVersion" value={redsysParams.Ds_SignatureVersion} />
                    <input type="hidden" name="Ds_MerchantParameters" value={redsysParams.Ds_MerchantParameters} />
                    <input type="hidden" name="Ds_Signature" value={redsysParams.Ds_Signature} />
                </form>
            )}

            {loading ? (
                <Loader />
            ) : receipts.length === 0 ? (
                <Card>
                    <Card.Body className='p-5 text-center'>
                        <p className="mb-0">{t('receiptTranslate.noPendingReceipts')}</p>
                    </Card.Body>
                </Card>
            ) : (
                <Row xs={1} md={2} lg={3} className="g-3">
                    {receipts.map((receipt) => {
                        const isPaid = paidReceiptIds.includes(String(receipt.id));

                        return (
                            <Col key={receipt.id}>
                                <Card className="h-100 shadow-sm">
                                    <Card.Header
                                        className="d-flex justify-content-between align-items-center text-white"
                                        style={{ backgroundColor: 'var(--color1, #821938)', borderColor: 'var(--color1, #821938)' }}
                                    >
                                        <div className="fw-bold fs-6.5"># recibo: {receipt?.numero}</div>
                                        <div className="fw-bold fs-6.5">Poliza: {receipt?.numeroPoliza}</div>
                                    </Card.Header>
                                    <Card.Body className="d-flex flex-column">
                                        <Card.Title className="mb-3">
                                            {receipt.nombreCompania}
                                        </Card.Title>
                                        <Row className="mb-2">
                                            <Col xs={6}>
                                                <small className="text-muted d-block">{t('receiptTranslate.effectDate')}</small>
                                                {(() => {
                                                    if (!receipt?.fechaEfecto) return "—";
                                                    const raw = receipt.fechaEfecto;
                                                    // Intentamos arreglar años con más de 4 dígitos
                                                    const fixed = raw.replace(/^(\d{4})\d+/, "$1");
                                                    const date = new Date(fixed);
                                                    if (isNaN(date.getTime())) return "—";
                                                    return new Intl.DateTimeFormat('es-ES', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        timeZone: 'Europe/Madrid'
                                                    }).format(date);
                                                })()}
                                            </Col>
                                            <Col xs={6}>
                                                <small className="text-muted d-block">{t('receiptTranslate.expirationDate')}</small>
                                                {(() => {
                                                    if (!receipt?.fechaVencimiento) return "—";
                                                    const raw = receipt.fechaVencimiento;
                                                    // Intentamos arreglar años con más de 4 dígitos
                                                    const fixed = raw.replace(/^(\d{4})\d+/, "$1");
                                                    const date = new Date(fixed);
                                                    if (isNaN(date.getTime())) return "—";
                                                    return new Intl.DateTimeFormat('es-ES', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        timeZone: 'Europe/Madrid'
                                                    }).format(date);
                                                })()}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col xs={6}>
                                                <small className="text-muted d-block">{t('receiptTranslate.importeTotal')}</small>
                                                <strong>{formatAmount(receipt.importeTotal)}</strong>
                                            </Col>
                                            <Col xs={6}>
                                                <small className="text-muted d-block">{t('receiptTranslate.estado')}</small>
                                                <span>{receipt.estado}</span>
                                            </Col>
                                        </Row>

                                        <div className="mt-auto">
                                            <Button
                                                variant={isPaid ? "secondary" : "primary"}
                                                className="w-100"
                                                onClick={() => handlePay(receipt.id, receipt.importeTotal)}
                                                disabled={payingReceiptId === receipt.id || isPaid}
                                            >
                                                {payingReceiptId === receipt.id
                                                    ? <span className="spinner-border spinner-border-sm align-middle ms-2" />
                                                    : isPaid
                                                        ? t('receiptTranslate.pendingConfirmation')
                                                        : t('global.pay')
                                                }
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}

                </Row>
            )}
        </>
    )
}